import get from 'lodash/get';
import filter from 'lodash/filter';
import has from 'lodash/has';
import find from 'lodash/find';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import concat from 'lodash/concat';
import map from 'lodash/map';
import flatten from 'lodash/flatten';
import { ITEM_TYPE_FOLDER } from 'box-ui-elements/es/common/constants';
import type { KeyValue, State } from './builder-type';
import {
  ELEMENT_DESCRIPTION_ID,
  ELEMENT_EMAIL_ID,
  FORM_CONTENT_PATH,
  TYPE_UPLOAD_FIELD,
  FORM_PATH,
  FOLDER_URL,
  FILE_REQUEST_STATUS,
  NUM_LOADING_ROWS,
  FILE_REQUEST_VISIBILITY,
  MODE,
  DEFAULT_BASE_URL,
  DEFAULT_MAX_SELECTIONS,
} from '../../../constants';

import type { BoxItem, PlaceholderBoxItem } from '../../../types/Item';
import type { Element, UploadFieldElement } from '../../../types/form/Element';
import { removeUndefinedDeep } from '../../../common/utils/removeUndefined';
import type { FormContent } from '../../../types/form/Form';

type ElementIdAndGroupId = {
  elementId: string;
  groupId: string;
};

const selectors = {
  getFileRequestId: (state: State) => {
    return get(state, `workingFileRequest.id`, '');
  },
  getItemLayoutById: (
    state: State,
    elementIdAndGroupId: ElementIdAndGroupId,
  ) => {
    const { elementId, groupId } = elementIdAndGroupId;
    const layouts = get(
      state,
      `workingFileRequest.form.content.layouts.${groupId}.layout`,
    );
    return find(layouts, { i: elementId });
  },
  getFileRequestUrlId: (state: State) => {
    return get(state, `workingFileRequest.urlId`, '');
  },
  getFolderUrl: (state: State) => {
    return `${FOLDER_URL}${get(state, `workingFileRequest.folder.id`, '')}`;
  },
  getFormEditUrl: (state: State) => {
    return `${DEFAULT_BASE_URL}/edit/${selectors.getFileRequestId(state)}}`;
  },
  getFormVersionId: (state: State) => {
    return get(state, `${FORM_PATH}.versionId`, '');
  },
  getFormContent: (state: State): FormContent => {
    return get(state, `${FORM_CONTENT_PATH}`, {
      root: '',
      components: {},
      theme: {
        colors: {},
        logo: {
          key: undefined,
          value: undefined,
        },
      },
    });
  },
  getComponentForId: (state: State, id: string) => {
    return get(state, `${FORM_CONTENT_PATH}.components.${id}`);
  },
  getElementTestid: (state: State, id: string): string => {
    const testid = get(state, `formElementTestids.${id}`, '');
    return testid;
  },
  getPageInfoForGroupId: (state: State, groupId: string) => {
    return get(state, `${FORM_CONTENT_PATH}.layouts.${groupId}`);
  },
  getItemsForGroupId: (state: State, groupId: string) => {
    return get(state, `${FORM_CONTENT_PATH}.components.${groupId}.items`, []);
  },
  getPreviewUrl: (state: State) => {
    const isActive =
      get(state, `workingFileRequest.status`) === FILE_REQUEST_STATUS.ACTIVE;
    const { protocol, hostname } = window.location;
    let path;
    if (isActive) {
      path = get(state, `workingFileRequest.url`, '');
    } else {
      const urlId = get(state, `workingFileRequest.urlId`, '');
      path = `/filerequest/preview/${urlId}`;
    }
    return `${protocol}//${hostname}${path}`;
  },
  getFormPreviewUrl: (fileRequestId: string) => {
    const { protocol, hostname, port } = window.location;
    const portPath = port ? `:${port}` : '';
    const baseUrl = `${protocol}//${hostname}${portPath}`;

    const path = `/relay/forms/preview/${fileRequestId}`;
    return `${baseUrl}${path}`;
  },
  getRootId: (state: State) => {
    const id = get(state, `${FORM_CONTENT_PATH}.root`, '');
    return id;
  },
  getTitle: (state: State) => {
    const rootId = get(state, `${FORM_CONTENT_PATH}.root`);
    const title = get(
      state,
      `${FORM_CONTENT_PATH}.components.${rootId}.label`,
      '',
    );
    return title;
  },
  isTitleElement: (state: State, elementId: string) => {
    const rootId = get(state, `${FORM_CONTENT_PATH}.root`);
    return rootId === elementId;
  },
  getDescription: (state: State) => {
    const rootId = get(state, `${FORM_CONTENT_PATH}.root`);
    const description = get(
      state,
      `${FORM_CONTENT_PATH}.components.${rootId}.description`,
      '',
    );
    return description;
  },
  getUsernameById: (state: State, id: string) => {
    const user = get(
      state,
      `workingFileRequest.users.${id}.public_name`,
      undefined,
    );
    return user;
  },
  getMetadataFieldForElementId: (state: State, id: string) => {
    const fieldKey = get(
      state,
      `${FORM_CONTENT_PATH}.components.${id}.metadataFieldKey`,
      undefined,
    );
    const fields = get(state, 'activeMetadataTemplates[0].fields', []);
    return find(fields, f => f.key === fieldKey);
  },
  getActiveMetadataTemplate: (state: State) => {
    return get(state, 'activeMetadataTemplates[0]', undefined);
  },
  getActiveMetadataTemplates: (state: State) => {
    return get(state, 'activeMetadataTemplates');
  },
  getAllMetadataTemplates: (state: State) => {
    return get(state, 'allMetadataTemplates', []);
  },
  getMetadataTemplateName: (state: State) => {
    return get(state, 'activeMetadataTemplates[0].displayName', '');
  },
  getUnselectedMetadataFields: (state: State) => {
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    return filter<any>(
      get(state, 'activeMetadataTemplates[0].fields', []),
      field => field.isSelected !== true && !field.isHidden,
    );
  },
  // this selector should only be used when multiple metadata templates are enabled
  hasUnselectedMetadataFields: (state: State) => {
    if (!get(state, 'allMetadataTemplates', [])) {
      return false;
    }
    if (!get(state, 'activeMetadataTemplates', [])) {
      return true;
    }
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    const unselectedFields = filter<any>(
      flatten(
        map(
          get(state, 'activeMetadataTemplates', []),
          metadataTemplate => metadataTemplate?.fields,
        ),
      ),
      field => field.isSelected !== true && !field.isHidden,
    );
    const activeTemplateIds = map(
      get(state, 'activeMetadataTemplates', []),
      'id',
    );
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    const unusedTemplates = filter<any>(
      get(state, 'allMetadataTemplates', []),
      template => !includes(activeTemplateIds, template.id),
    );
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    const unusedTemplateFields = filter<any>(
      flatten(
        map(unusedTemplates, metadataTemplate => metadataTemplate?.fields),
      ),
      field => field.isSelected !== true && !field.isHidden,
    );
    return unselectedFields.length > 0 || unusedTemplateFields.length > 0;
  },
  hasUnsavedChanges: (state: State) => {
    // Lodash isEqual consider undefined values in equality checks
    const sanitizedWorkingFileRequest = removeUndefinedDeep(
      state.workingFileRequest,
    );

    const sanitizedSavedFileRequest = removeUndefinedDeep(
      state.savedFileRequest,
    );
    return !isEqual(sanitizedSavedFileRequest, sanitizedWorkingFileRequest);
  },
  isNewForm: (state: State, mode: string) => {
    const fileRequestId = selectors.getFileRequestId(state);
    const formVersionId = selectors.getFormVersionId(state);
    return mode === MODE.FORM && !fileRequestId && !formVersionId;
  },
  oneRequiredUploadFieldRemaining: (state: State) => {
    const components = get(state, `${FORM_CONTENT_PATH}.components`);
    if (components) {
      const numberOfRequiredUploadField = filter(
        components,
        (component: Element) =>
          component.type === TYPE_UPLOAD_FIELD && !!component.required,
      ).length;
      return numberOfRequiredUploadField === 1;
    }
    return false;
  },
  isMetadataTemplateSelected: (state: State) => {
    const metadataTemplate = get(state, `activeMetadataTemplates`, []);
    return metadataTemplate.length > 0;
  },
  getEmailField: (state: State) => {
    const components = get(state, `${FORM_CONTENT_PATH}.components`, {});
    return get(components, ELEMENT_EMAIL_ID);
  },
  getDescriptionField: (state: State) => {
    const components = get(state, `${FORM_CONTENT_PATH}.components`, {});
    return get(components, ELEMENT_DESCRIPTION_ID);
  },
  hasEmailField: (state: State) => {
    const components = get(state, `${FORM_CONTENT_PATH}.components`, {});
    const emailField = get(components, ELEMENT_EMAIL_ID);
    return !!emailField && get(emailField, 'visible', false);
  },
  isAuthenticationRequired: (state: State) => {
    const visibilityAuthenticated =
      get(state, 'savedFileRequest.visibility') ===
      FILE_REQUEST_VISIBILITY.AUTHENTICATED;
    return visibilityAuthenticated;
  },
  hasDescriptionField: (state: State) => {
    const components = get(state, `${FORM_CONTENT_PATH}.components`, {});
    const descriptionField = get(components, ELEMENT_DESCRIPTION_ID);
    return !!descriptionField && get(descriptionField, 'visible', false);
  },
  isDescriptionEditable: (
    state: State,
    id: string,
    blueprintEnabled: boolean,
  ) => {
    if (blueprintEnabled) {
      return true;
    }
    return (
      includes(state.editableDescriptions, id) ||
      !!get(state, `${FORM_CONTENT_PATH}.components.${id}.description`)
    );
  },
  getOptions: (state: State, id: string) => {
    const options = get(
      state,
      `${FORM_CONTENT_PATH}.components.${id}.options`,
      [],
    );
    return options;
  },
  getNumUploadZones: (state: State) => {
    const components = get(state, `${FORM_CONTENT_PATH}.components`, {});
    return filter(
      components,
      (element: Element) => element.type === TYPE_UPLOAD_FIELD,
    ).length;
  },
  getUploadFields: (state: State): Array<UploadFieldElement> => {
    const components = get(state, `${FORM_CONTENT_PATH}.components`, {});
    return filter(
      components,
      (element: Element) => element.type === TYPE_UPLOAD_FIELD,
    );
  },
  getUploadFieldIds: (state: State): Array<string> => {
    return Object.keys(state.workingFileRequest.form.content.components).reduce<
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
      Array<any>
    >((fieldIds, componentId) => {
      if (
        state.workingFileRequest.form.content.components[componentId]?.type ===
        TYPE_UPLOAD_FIELD
      ) {
        fieldIds.push(componentId);
      }

      return fieldIds;
    }, []);
  },
  getUploadFieldIdsWithInvalidFolderAssociated: (state: State) => {
    const uploadFields = selectors.getUploadFields(state);
    const folderInfoById = get(state, 'workingFileRequest.folderInfoById', {});
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    return uploadFields.reduce<Array<any>>((invalidIds, field) => {
      const folderId = get(field, 'folderId', '');
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7053
      const folderInfo = folderInfoById[folderId];
      // if folderInfo is undefined (folder deleted) or canUserAssociateFormUploadFiles is false (user no longer has access to folder)
      if (!folderInfo || !folderInfo.canUserAssociateFormUploadFiles) {
        invalidIds.push(field.id);
      }
      return invalidIds;
    }, []);
  },
  getNumMetadataFields: (state: State) => {
    const components = get(state, `${FORM_CONTENT_PATH}.components`, {});
    return filter(components, element => has(element, 'metadataTemplateId'))
      .length;
  },
  getMetadataFieldIds: (state: State): Array<string> => {
    return Object.keys(state.workingFileRequest.form.content.components).reduce(
      (fieldIds: string[], componentId) => {
        if (
          state.workingFileRequest.form.content.components[componentId]
            ?.metadataFieldKey
        ) {
          fieldIds.push(componentId);
        }

        return fieldIds;
      },
      [],
    );
  },
  getFolderTreeItems: (state: State) => {
    const {
      contentPickerState: { items, requestInProgress },
    } = state;

    let folderTreeItems = map<BoxItem, BoxItem>(items, item => {
      const isDisabled =
        item.isExternallyOwned ||
        !item.canUserSeeFileRequestFeature ||
        item.type !== ITEM_TYPE_FOLDER;
      const isActionDisabled =
        item.isExternallyOwned ||
        !item.canUserSeeFileRequestFeature ||
        item.hasFileRequest;
      return {
        ...item,
        hasCollaborations: item.isCollaborated,
        isActionDisabled,
        isDisabled,
      };
    });

    if (requestInProgress) {
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
      folderTreeItems = concat<BoxItem | PlaceholderBoxItem>(
        folderTreeItems,
        Array(NUM_LOADING_ROWS).fill({
          isLoading: true,
        }),
      );
    }
    return folderTreeItems;
  },
  getFolderTreeItemsForms: (state: State) => {
    const {
      contentPickerState: { items, requestInProgress },
    } = state;

    let folderTreeItems = map<BoxItem, BoxItem>(items, item => {
      const isDisabled =
        item.isExternallyOwned ||
        !item.canUserAssociateFormUploadFiles ||
        item.type !== ITEM_TYPE_FOLDER;
      const isActionDisabled =
        item.isExternallyOwned || !item.canUserAssociateFormUploadFiles;
      return {
        ...item,
        hasCollaborations: item.isCollaborated,
        isActionDisabled,
        isDisabled,
      };
    });

    if (requestInProgress) {
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
      folderTreeItems = concat<BoxItem | PlaceholderBoxItem>(
        folderTreeItems,
        Array(NUM_LOADING_ROWS).fill({
          isLoading: true,
        }),
      );
    }
    return folderTreeItems;
  },
  getThemeLogo: (state: State): KeyValue => {
    return get(state, 'workingFileRequest.form.content.theme.logo', {
      key: undefined,
      value: undefined,
    });
  },
  getThemeLogoKey: (state: State) => {
    return get(state, 'workingFileRequest.form.content.theme.logo.key', '');
  },
  getThemeLogoValue: (state: State) => {
    return get(state, 'workingFileRequest.form.content.theme.logo.value', '');
  },
  getThemePrimaryColor: (state: State): KeyValue => {
    return get(state, 'workingFileRequest.form.content.theme.colors.primary', {
      key: undefined,
      value: undefined,
    });
  },
  getThemePrimaryColorValue: (state: State) => {
    return get(
      state,
      'workingFileRequest.form.content.theme.colors.primary.value',
      '',
    );
  },
  getDraggingFormField: (state: State) => {
    return get(state, 'dragDropNewFormFieldState');
  },
  getElementBeingDragged: (state: State) => {
    return get(state, 'elementBeingDragged', '');
  },
  getElementInFocus: (state: State) => {
    return get(state, 'elementInFocus', '');
  },
  isExistingFormFieldDragging: (state: State) => {
    return get(state, 'isItemDragging');
  },
  getElementHeightState: (state: State) => {
    return get(state, `formElementsHeightState`, {});
  },
  getMaximumSelectionsForId: (state: State, id: string) => {
    return get(
      state,
      `${FORM_CONTENT_PATH}.components.${id}.maximumSelections`,
      DEFAULT_MAX_SELECTIONS,
    );
  },
  canUserEditTheme: (state: State) => {
    const type = get(state, 'savedFileRequest.type');
    switch (type) {
      case MODE.FILE_REQUEST:
        return get(
          state,
          'savedFileRequest.fileRequestBrandingUserEnabled',
          false,
        );
      case MODE.FORM:
        return get(state, 'savedFileRequest.formsBrandingUserEnabled', false);
      default:
    }
    return false;
  },
  getUploadLogoModalFile: (state: State) => {
    return get(state, 'stagedThemeUploadedLogo', '');
  },
  getFormLogic: (state: State) => {
    return get(state, 'workingFileRequest.form.content.logic', []);
  },
};

export default selectors;
